import { getLocaleForPublicReview } from 'utils/getLocaleId'
import { formatNumber } from 'utils/format'

/**
 * Отфильтровывает пустые значения из массива и объединяет их в строку
 * @param {Array} arr - исходный массив
 * @param {string} separator - разделитель (по умолчанию, запятая)
 * @return {string}
 */
export const joinValues = (arr, separator = ', ') => {
  return arr.filter(Boolean).join(separator)
}

/**
 * Форматирует volumeML продукта
 * @param  {number|undefined} volumeML - объем бутылки в мл
 * @param  {{mlFormatter: (v: string) => any}} options
 * @return {string} - объем бутылки с единицами измерения
 */
export const formatBottleSize = (
  volumeML,
  options = { mlFormatter: v => v }
) => {
  const { mlFormatter } = options
  if (!volumeML) return ''
  const val = ['number', 'string'].includes(typeof volumeML)
    ? volumeML
    : volumeML?.volumeML ?? ''
  if (val >= 1000) return formatNumber(val / 1000) + 'L'
  return mlFormatter(val + 'ML')
}

/**
 * Получает ссылку на страницу просмотра вина
 * @param wineId {string}
 * @return {string}
 */
export const getWineViewUrl = wineId => {
  return wineId ? `/acc/wine/${wineId}` : ''
}

/**
 * Получает ссылку на страницу просмотра продукта
 * @param slug {string}
 * @return {string}
 */
export const getProductViewUrl = slug => {
  return slug ? `/acc/product/${slug}` : '/acc'
}

export const getProductReviewUrl = (slug, reviewId) => {
  if (!slug || !reviewId) return ''
  return `/acc/product/${slug}/review/${reviewId}`
}

export const getProductReviewEditUrl = reviewId => {
  return reviewId ? `/acc/review/${reviewId}/edit` : ''
}

export const getPublicReviewUrl = (slug, reviewId) => {
  if (!slug || !reviewId) return ''
  const lang = getLocaleForPublicReview()
  return `${window.location.origin}/${lang}/product/${slug}/review/${reviewId}`
}

/**
 * Получает ссылку на страницу создания вина
 * @return {string}
 */
export const getWineCreateUrl = () => {
  return '/acc/wine/create'
}

/**
 * Получает ссылку на страницу редактирования вина
 * @param wineId {number}
 * @param tab {'identification'|'general'|'packaging'|'awards'|'techsheets'}
 * @return {string}
 */
export const getWineEditUrl = (wineId, tab = 'general') => {
  if (!wineId) return '/acc'
  return `/acc/wine/${wineId}/edit/${tab}`
}

/**
 * Получает строку с информацией wineRegion вина
 * @param wine {object}
 * @return {string}
 */
export const getWineRegionString = wine => {
  const getRegionsFromGeoIndication = item =>
    joinValues((item?.wineRegionsEU ?? []).map(region => region?.localizedName))

  return joinValues(
    (wine?.geoIndications ?? []).map(getRegionsFromGeoIndication)
  )
}

/**
 * Получает строку с информацией wineRegion для GI
 * @param wine {object}
 * @return {string}
 */
export const getWineRegionGI = wine => {
  const getRegionsFromGeoIndication = item => item?.wineRegionEU?.localizedName

  return joinValues(
    (wine?.geoIndications ?? []).map(getRegionsFromGeoIndication)
  )
}

/**
 * Получает строку с информацией nationalGIType вина
 * @param wine {object}
 * @return {string}
 */
export const getWineNationalGITypeString = wine => {
  return (
    wine?.nationalGITypeText ??
    wine?.nationalGIType?.localizedShortName ??
    wine?.nationalGIType?.localizedName ??
    ''
  )
}

/**
 * Получает строку с информацией geoIndications вина
 */
export const getWineGeoIndicationString = (
  wine: Record<string, any> | undefined | null,
  separator = ', '
): string => {
  return (
    wine?.geoIndicationText ??
    joinValues(
      (wine?.geoIndications ?? []).map(
        item => item?.localizedShortName || item?.localizedName
      ),
      separator
    )
  )
}

/**
 * Получает строку с информацией geoIndication и national GI type
 * @param wine {object}
 * @return {string}
 */
export const getWineGI = wine => {
  return joinValues(
    [getWineGeoIndicationString(wine), getWineNationalGITypeString(wine)],
    ' '
  )
}

/**
 * Получает строку с информацией specialClassifications вина
 * @param wine {object}
 * @param separator {string} - default ', '
 * @return {string}
 */
export const getWineSpecialClassificationsString = (wine, separator) => {
  return joinValues(
    (wine?.specialClassifications ?? []).map(item => item?.name),
    separator
  )
}

/**
 * Получает контакты для вина
 * @param wine {object}
 * @return {{tradeName: string, website: string, phone: string, email: string}}
 */
export const getWineContacts = wine => {
  const {
    contactProducerTradeName,
    contactWebsite,
    contactPhone,
    contactEmail,
  } = wine?.techSheet || {}

  const isCustomContacts =
    (contactProducerTradeName ??
      contactWebsite ??
      contactPhone ??
      contactEmail ??
      null) !== null

  if (isCustomContacts) {
    return {
      tradeName: contactProducerTradeName || '',
      website: contactWebsite || '',
      phone: contactPhone || '',
      email: contactEmail || '',
    }
  }

  const tradeName = wine?.tradeName
  return {
    tradeName: tradeName?.tradeName || '',
    website: tradeName?.website || '',
    phone: tradeName?.phone || '',
    email: tradeName?.email || '',
  }
}

/**
 * Получает строку с информацией wineType вина
 * @param wine {object}
 * @return {string}
 */
export const getWineTypeString = wine => {
  const wineType = wine?.wineType
  const isStillWine = wineType?.isDefault

  if (isStillWine) {
    return ''
  }

  return wineType?.localizedName ?? ''
}

/**
 * Получает строку с информацией sweetnessLevel вина
 * @param wine {object}
 * @return {string}
 */
export const getWineSweetnessString = wine => {
  const sweetnessLevel = wine?.sweetnessLevel
  const isStillWine = wine?.wineType?.isDefault
  const isDryWine = sweetnessLevel?.isDefault

  if (isDryWine && isStillWine) {
    return ''
  }

  return sweetnessLevel?.localizedName ?? ''
}

/**
 * Получает строку с информацией wineColor вина
 * @param wine {object}
 * @return {string}
 */
export const getWineColorString = wine => {
  return wine?.wineColor?.localizedName ?? wine?.color?.localizedName ?? ''
}

/**
 * Получает массив grapeVarieties вина отсортированных по значению и наименованию
 * @param wine {object}
 * @return {Array<object>}
 */
export const getWineGrapeVarieties = wine => {
  return [...(wine?.grapeVarieties ?? [])].sort((a, b) => {
    const valueA = a.value ?? 0
    const valueB = b.value ?? 0
    if (valueA === valueB) {
      const nameA = a.grapeVarietySynonym?.name ?? ''
      const nameB = b.grapeVarietySynonym?.name ?? ''
      return nameB.localeCompare(nameA)
    }

    return valueB - valueA
  })
}

/**
 * Получает массив строк из grapeVarieties вина
 * @param wine {object}
 * @return {string[]}
 */
export const getWineGrapeVarietiesStrings = wine => {
  return getWineGrapeVarieties(wine).map(item => {
    let result = item.grapeVarietySynonym?.name ?? ''
    if (item.value) {
      result += ` (${item.value}%)`
    }
    return result
  })
}

/**
 * Получает название вина по tradeNameBrand
 * @param wine {object}
 * @return {string}
 */
export const getWineTitleTradeNameBrand = wine => {
  const tradeNameBrand =
    wine?.tradeNameBrand ??
    wine?.producer?.tradeName ?? // wine/:id/edit
    wine?.tradeName?.tradeName
  const keyWords = wine?.keyWords
  const specialClassifications = wine?.specialClassifications ?? []
  const classificationsText = specialClassifications.map(i => i?.name)
  const vintage = wine?.vintageText

  return joinValues(
    [tradeNameBrand, keyWords, ...classificationsText, vintage],
    ' '
  )
}

/**
 * Получает название вина по brand
 * @param wine {object}
 * @return {string}
 */
export const getWineTitleBrand = wine => {
  const brand = wine?.brand?.name ?? wine?.brand?.brand
  const keyWords = wine?.keyWords
  const specialClassifications = wine?.specialClassifications ?? []
  const classificationsText = specialClassifications.map(i => i?.name)
  const vintage = wine?.vintageText

  return joinValues([brand, keyWords, ...classificationsText, vintage], ' ')
}

/**
 * Получает объект для передачи его в location.state
 * @param wine {object}
 * @return {object}
 */
export const getAddVintageState = wine => {
  return {
    addNewWine: true,
    producer: wine?.tradeName,
    producerCountry: wine?.tradeName?.country,
    wineCountry: wine?.country,
    brand: wine?.brand && {
      ...wine?.brand,
      localizedName: wine?.brand?.name,
    },
    wineType: wine?.wineType,
    wineColour: wine?.wineColor,
    sweetness: wine?.sweetnessLevel,
    nationalGIType: wine?.nationalGIType,
    geoIndication: wine?.geoIndications,
    specialClassification: wine?.specialClassifications,
    additionalName: wine?.keyWords,
  }
}
